<template>
  <div class="tax-application-management">
    <div class="header">
      <h2>税务代办申请管理</h2>
      <div class="actions">
        <el-button type="primary" icon="el-icon-plus" @click="handleAdd">新建申请</el-button>
        <el-button v-if="canBatchProcess" type="warning" icon="el-icon-s-operation"
          @click="handleBatchProcess">批量操作</el-button>
        <el-button type="success" icon="el-icon-setting" @click="handleStatusConfig">状态配置</el-button>
      </div>
    </div>

    <!-- 搜索筛选 -->
    <el-card class="search-card">
      <el-form :model="searchForm" inline>
        <el-form-item label="关键字">
          <el-input v-model="searchForm.keyword" placeholder="搜索人名/编号/类型" clearable @clear="handleSearch"
            @keyup.enter.native="handleSearch"></el-input>
        </el-form-item>
        <el-form-item label="申请编号">
          <el-input v-model="searchForm.applicationNumber" placeholder="请输入申请编号" clearable></el-input>
        </el-form-item>
        <el-form-item label="企业">
          <el-select v-model="searchForm.enterpriseId" placeholder="请选择企业" clearable filterable>
            <el-option v-for="enterprise in enterprises" :key="enterprise.id" :label="enterprise.name"
              :value="enterprise.id"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="申请类型">
          <el-select v-model="searchForm.applicationType" placeholder="请选择申请类型" clearable>
            <el-option label="税务登记" value="TAX_REGISTRATION"></el-option>
            <el-option label="税务申报" value="TAX_DECLARATION"></el-option>
            <el-option label="发票申请" value="INVOICE_APPLICATION"></el-option>
            <el-option label="税务注销" value="TAX_CANCELLATION"></el-option>
            <el-option label="其他" value="OTHER"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="申请状态">
          <el-select v-model="searchForm.status" placeholder="请选择状态" clearable>
            <el-option label="待处理" value="PENDING"></el-option>
            <el-option label="处理中" value="PROCESSING"></el-option>
            <el-option label="已完成" value="COMPLETED"></el-option>
            <el-option label="已拒绝" value="REJECTED"></el-option>
            <el-option label="已取消" value="CANCELLED"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="处理人员">
          <el-select v-model="searchForm.processorId" placeholder="请选择处理人员" clearable>
            <el-option v-for="processor in processors" :key="processor.id" :label="processor.username"
              :value="processor.id"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">搜索</el-button>
          <el-button @click="handleReset">重置</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 申请列表 -->
    <el-table :data="applications" v-loading="loading" border style="width: 100%"
      @selection-change="handleSelectionChange">
      <el-table-column v-if="canBatchProcess" type="selection" width="55"></el-table-column>
      <el-table-column prop="applicationNumber" label="申请编号" width="160"></el-table-column>
      <el-table-column prop="enterpriseName" label="企业名称" min-width="200"></el-table-column>
      <el-table-column prop="applicantName" label="申请人" width="120"></el-table-column>
      <el-table-column prop="applicationType" label="申请类型" width="120">
        <template slot-scope="scope">
          {{ getApplicationTypeText(scope.row.applicationType) }}
        </template>
      </el-table-column>
      <el-table-column prop="status" label="状态" width="100">
        <template slot-scope="scope">
          <el-tag :type="getStatusType(scope.row.status)">
            {{ getStatusText(scope.row.status) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="processorName" label="处理人员" width="120"></el-table-column>
      <el-table-column prop="submitDate" label="提交时间" width="160">
        <template slot-scope="scope">
          {{ formatDateTime(scope.row.submitDate) }}
        </template>
      </el-table-column>
      <el-table-column prop="estimatedCompletionTime" label="预计完成" width="160">
        <template slot-scope="scope">
          {{ formatDateTime(scope.row.estimatedCompletionTime) }}
        </template>
      </el-table-column>
      <el-table-column label="操作" width="250" fixed="right">
        <template slot-scope="scope">
          <el-button size="mini" @click="handleView(scope.row)">查看</el-button>
          <el-button v-if="scope.row.status === 'PENDING'" size="mini" type="primary"
            @click="handleEdit(scope.row)">编辑</el-button>
          <el-button v-if="canProcess" size="mini" type="success" @click="handleProcess(scope.row)">处理</el-button>
          <el-button size="mini" type="info" @click="handleStatusHistory(scope.row)">历史</el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <el-pagination @size-change="handleSizeChange" @current-change="handleCurrentChange" :current-page="pagination.page"
      :page-sizes="[10, 20, 50, 100]" :page-size="pagination.size" layout="total, sizes, prev, pager, next, jumper"
      :total="pagination.total" style="margin-top: 20px; text-align: right"></el-pagination>

    <!-- 新建/编辑申请对话框 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="800px" @close="handleDialogClose">
      <el-form :model="applicationForm" :rules="rules" ref="applicationForm" label-width="120px">
        <el-form-item label="企业" prop="enterpriseId" v-if="!isEdit">
          <el-select v-model="applicationForm.enterpriseId" placeholder="请选择企业" filterable>
            <el-option v-for="enterprise in enterprises" :key="enterprise.id" :label="enterprise.name"
              :value="enterprise.id"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="申请类型" prop="applicationType">
          <el-select v-model="applicationForm.applicationType" placeholder="请选择申请类型">
            <el-option label="税务登记" value="TAX_REGISTRATION"></el-option>
            <el-option label="税务申报" value="TAX_DECLARATION"></el-option>
            <el-option label="发票申请" value="INVOICE_APPLICATION"></el-option>
            <el-option label="税务注销" value="TAX_CANCELLATION"></el-option>
            <el-option label="其他" value="OTHER"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="申请内容" prop="applicationContent">
          <el-input type="textarea" v-model="applicationForm.applicationContent" placeholder="请详细描述申请内容（JSON格式）"
            :rows="4"></el-input>
        </el-form-item>
        <el-form-item label="申请描述" prop="description">
          <el-input type="textarea" v-model="applicationForm.description" placeholder="请输入申请描述" :rows="3"></el-input>
        </el-form-item>
        <el-form-item label="预计完成时间" prop="estimatedCompletionTime">
          <el-date-picker v-model="applicationForm.estimatedCompletionTime" type="datetime" placeholder="选择预计完成时间"
            value-format="yyyy-MM-dd HH:mm:ss" style="width: 100%"></el-date-picker>
        </el-form-item>
        <el-form-item label="附件">
          <el-upload :action="uploadConfig.action" :headers="uploadConfig.headers" :file-list="fileList"
            :on-success="handleFileSuccess" :on-remove="handleFileRemove" multiple>
            <el-button size="small" type="primary">点击上传</el-button>
            <div slot="tip" class="el-upload__tip">支持上传多个文件，单个文件大小不超过10MB</div>
          </el-upload>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit" :loading="submitting">提交申请</el-button>
      </span>
    </el-dialog>

    <!-- 申请详情对话框 -->
    <el-dialog title="申请详情" :visible.sync="detailDialogVisible" width="900px">
      <div v-if="currentApplication">
        <el-descriptions :column="2" border>
          <el-descriptions-item label="申请编号">{{ currentApplication.applicationNumber }}</el-descriptions-item>
          <el-descriptions-item label="企业名称">{{ currentApplication.enterpriseName }}</el-descriptions-item>
          <el-descriptions-item label="申请人">{{ currentApplication.applicantName }}</el-descriptions-item>
          <el-descriptions-item label="申请类型">{{ getApplicationTypeText(currentApplication.applicationType)
          }}</el-descriptions-item>
          <el-descriptions-item label="申请状态">
            <el-tag :type="getStatusType(currentApplication.status)">
              {{ getStatusText(currentApplication.status) }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="处理人员">{{ currentApplication.processorName || '未分配' }}</el-descriptions-item>
          <el-descriptions-item label="提交时间">{{ formatDateTime(currentApplication.submitDate) }}</el-descriptions-item>
          <el-descriptions-item label="预计完成时间">{{ formatDateTime(currentApplication.estimatedCompletionTime)
          }}</el-descriptions-item>
          <el-descriptions-item label="接受时间" v-if="currentApplication.acceptDate">{{
            formatDateTime(currentApplication.acceptDate) }}</el-descriptions-item>
          <el-descriptions-item label="完成时间" v-if="currentApplication.completeDate">{{
            formatDateTime(currentApplication.completeDate) }}</el-descriptions-item>
        </el-descriptions>

        <div style="margin-top: 20px;">
          <h4>申请内容</h4>
          <pre>{{ formatJSON(currentApplication.applicationContent) }}</pre>
        </div>

        <div style="margin-top: 20px;">
          <h4>申请描述</h4>
          <p>{{ currentApplication.description || '无描述' }}</p>
        </div>

        <div v-if="currentApplication.processRemarks" style="margin-top: 20px;">
          <h4>处理备注</h4>
          <p>{{ currentApplication.processRemarks }}</p>
        </div>

        <div v-if="currentApplication.rejectReason" style="margin-top: 20px;">
          <h4>拒绝原因</h4>
          <p style="color: #f56c6c;">{{ currentApplication.rejectReason }}</p>
        </div>

        <div v-if="currentApplication.attachmentUrls && currentApplication.attachmentUrls.length > 0"
          style="margin-top: 20px;">
          <h4>附件</h4>
          <div v-for="(url, index) in currentApplication.attachmentUrls" :key="index">
            <el-link :href="url" target="_blank">附件{{ index + 1 }}</el-link>
          </div>
        </div>

        <div v-if="currentApplication.resultFileUrls && currentApplication.resultFileUrls.length > 0"
          style="margin-top: 20px;">
          <h4>处理结果文件</h4>
          <div v-for="(url, index) in currentApplication.resultFileUrls" :key="index">
            <el-link :href="url" target="_blank">结果文件{{ index + 1 }}</el-link>
          </div>
        </div>
      </div>
    </el-dialog>

    <!-- 状态处理对话框 -->
    <el-dialog title="处理申请" :visible.sync="processDialogVisible" width="600px">
      <el-form :model="processForm" :rules="processRules" ref="processForm" label-width="120px">
        <el-form-item label="状态变更" prop="newStatus">
          <el-select v-model="processForm.newStatus" placeholder="请选择新状态">
            <el-option v-for="status in availableStatuses" :key="status" :label="getStatusText(status)"
              :value="status"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="处理备注" prop="remarks">
          <el-input type="textarea" v-model="processForm.remarks" placeholder="请输入处理备注" :rows="4"></el-input>
        </el-form-item>
        <el-form-item label="预计完成时间" v-if="processForm.newStatus === 'PROCESSING'">
          <el-date-picker v-model="processForm.estimatedCompletionTime" type="datetime" placeholder="选择预计完成时间"
            value-format="yyyy-MM-dd HH:mm:ss" style="width: 100%"></el-date-picker>
        </el-form-item>
        <el-form-item label="结果文件" v-if="processForm.newStatus === 'COMPLETED'">
          <el-upload :action="uploadConfig.action" :headers="uploadConfig.headers" :file-list="resultFileList"
            :on-success="handleResultFileSuccess" :on-remove="handleResultFileRemove" multiple>
            <el-button size="small" type="primary">上传结果文件</el-button>
          </el-upload>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="processDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleProcessSubmit" :loading="processing">确定</el-button>
      </span>
    </el-dialog>

    <!-- 状态历史对话框 -->
    <el-dialog title="状态变更历史" :visible.sync="historyDialogVisible" width="80%" top="5vh">
      <el-table :data="statusHistory" v-loading="historyLoading" border style="width: 100%">
        <el-table-column prop="fromStatus" label="原状态" width="100">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.fromStatus" size="small">{{ getStatusText(scope.row.fromStatus) }}</el-tag>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column prop="toStatus" label="新状态" width="100">
          <template slot-scope="scope">
            <el-tag :type="getStatusType(scope.row.toStatus)" size="small">{{ getStatusText(scope.row.toStatus)
            }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="operatorName" label="操作人" width="120"></el-table-column>
        <el-table-column prop="operatorRole" label="操作角色" width="120"></el-table-column>
        <el-table-column prop="changeDate" label="变更时间" width="160">
          <template slot-scope="scope">
            {{ formatDateTime(scope.row.changeDate) }}
          </template>
        </el-table-column>
        <el-table-column prop="remarks" label="备注" min-width="200" show-overflow-tooltip></el-table-column>
        <el-table-column prop="changeReason" label="变更原因" min-width="150" show-overflow-tooltip></el-table-column>
        <el-table-column prop="isAutoTransition" label="自动流转" width="100">
          <template slot-scope="scope">
            <el-tag :type="scope.row.isAutoTransition ? 'success' : 'info'" size="small">
              {{ scope.row.isAutoTransition ? '是' : '否' }}
            </el-tag>
          </template>
        </el-table-column>
      </el-table>
    </el-dialog>

    <!-- 批量操作对话框 -->
    <el-dialog title="批量操作" :visible.sync="batchDialogVisible" width="500px">
      <el-form :model="batchForm" label-width="120px">
        <el-form-item label="操作类型">
          <el-select v-model="batchForm.operation" placeholder="请选择操作类型">
            <el-option label="分配处理人员" value="ASSIGN_PROCESSOR"></el-option>
            <el-option label="批量状态变更" value="CHANGE_STATUS"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="处理人员" v-if="batchForm.operation === 'ASSIGN_PROCESSOR'">
          <el-select v-model="batchForm.processorId" placeholder="请选择处理人员">
            <el-option v-for="processor in processors" :key="processor.id" :label="processor.username"
              :value="processor.id"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="目标状态" v-if="batchForm.operation === 'CHANGE_STATUS'">
          <el-select v-model="batchForm.targetStatus" placeholder="请选择目标状态">
            <el-option label="处理中" value="PROCESSING"></el-option>
            <el-option label="已完成" value="COMPLETED"></el-option>
            <el-option label="已拒绝" value="REJECTED"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="备注">
          <el-input type="textarea" v-model="batchForm.remarks" placeholder="请输入操作备注" :rows="3"></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="batchDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleBatchSubmit" :loading="batchProcessing">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'TaxApplicationManagement',
  data() {
    return {
      applications: [],
      enterprises: [],
      processors: [],
      statusHistory: [],
      availableStatuses: [],
      selectedApplications: [],
      loading: false,
      historyLoading: false,
      submitting: false,
      processing: false,
      batchProcessing: false,
      dialogVisible: false,
      detailDialogVisible: false,
      processDialogVisible: false,
      historyDialogVisible: false,
      batchDialogVisible: false,
      dialogTitle: '新建申请',
      isEdit: false,
      currentApplication: null,
      fileList: [],
      resultFileList: [],

      searchForm: {
        applicationNumber: '',
        enterpriseId: null,
        applicationType: '',
        status: '',
        processorId: null,
        keyword: ''
      },

      pagination: {
        page: 1,
        size: 20,
        total: 0
      },

      applicationForm: {
        id: null,
        enterpriseId: null,
        applicationType: '',
        applicationContent: '',
        description: '',
        estimatedCompletionTime: '',
        attachmentUrls: []
      },

      processForm: {
        newStatus: '',
        remarks: '',
        estimatedCompletionTime: '',
        resultFileUrls: []
      },

      batchForm: {
        operation: '',
        processorId: null,
        targetStatus: '',
        remarks: ''
      },

      rules: {
        enterpriseId: [
          { required: true, message: '请选择企业', trigger: 'change' }
        ],
        applicationType: [
          { required: true, message: '请选择申请类型', trigger: 'change' }
        ],
        applicationContent: [
          { required: true, message: '请输入申请内容', trigger: 'blur' }
        ],
        description: [
          { required: true, message: '请输入申请描述', trigger: 'blur' }
        ]
      },

      processRules: {
        newStatus: [
          { required: true, message: '请选择新状态', trigger: 'change' }
        ],
        remarks: [
          { required: true, message: '请输入处理备注', trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    uploadConfig() {
      return this.$http.getUploadConfig()
    },
    canProcess() {
      const userRole = localStorage.getItem('userRole')
      return userRole === 'TAX_PROCESSOR' || userRole === 'ADMIN' || userRole === 'SUPER_ADMIN'
    },
    canBatchProcess() {
      const userRole = localStorage.getItem('userRole')
      return userRole === 'ADMIN' || userRole === 'SUPER_ADMIN'
    }
  },
  mounted() {
    this.loadApplications()
    this.loadEnterprises()
    this.loadProcessors()
  },
  methods: {
    async loadApplications() {
      try {
        this.loading = true
        const params = {
          ...this.searchForm
        }
        const response = await this.$http.get('/enterprise-management/tax-applications', { params })
        if (response.success) {
          const allData = response.data || []
          this.pagination.total = response.total || allData.length || 0
          
          if (response.total && response.total > allData.length) {
            this.applications = allData
          } else {
            const start = (this.pagination.page - 1) * this.pagination.size
            const end = this.pagination.page * this.pagination.size
            this.applications = allData.slice(start, end)
          }
        } else {
          this.$message.error('加载申请列表失败: ' + response.message)
        }
      } catch (error) {
        console.error('加载申请列表失败:', error)
        this.$message.error('加载申请列表失败')
      } finally {
        this.loading = false
      }
    },

    async loadEnterprises() {
      try {
        const response = await this.$http.get('/enterprises')
        if (response.success) {
          this.enterprises = response.data || []
        }
      } catch (error) {
        console.error('加载企业列表失败:', error)
      }
    },

    async loadProcessors() {
      try {
        const response = await this.$http.get('/enterprise-management/tax-applications/processors')
        if (response.success) {
          this.processors = response.data || []
        }
      } catch (error) {
        console.error('加载处理人员列表失败:', error)
      }
    },

    async loadAvailableStatuses() {
      // 简化状态流转，直接返回所有可能的状态
      this.availableStatuses = ['PENDING', 'PROCESSING', 'COMPLETED', 'REJECTED', 'CANCELLED']
    },

    async loadStatusHistory(applicationId) {
      try {
        this.historyLoading = true
        const response = await this.$http.get(`/enterprise-management/tax-applications/${applicationId}/status-history`)
        if (response.success) {
          this.statusHistory = response.data || []
        } else {
          this.$message.error('加载状态历史失败: ' + response.message)
        }
      } catch (error) {
        console.error('加载状态历史失败:', error)
        this.$message.error('加载状态历史失败')
      } finally {
        this.historyLoading = false
      }
    },

    handleSearch() {
      this.pagination.page = 1
      this.loadApplications()
    },

    handleReset() {
      this.searchForm = {
        applicationNumber: '',
        enterpriseId: null,
        applicationType: '',
        status: '',
        processorId: null,
        keyword: ''
      }
      this.pagination.page = 1
      this.loadApplications()
    },

    handleSizeChange(size) {
      this.pagination.size = size
      this.loadApplications()
    },

    handleCurrentChange(page) {
      this.pagination.page = page
      this.loadApplications()
    },

    handleSelectionChange(selection) {
      this.selectedApplications = selection
    },

    handleAdd() {
      this.isEdit = false
      this.dialogTitle = '新建申请'
      this.dialogVisible = true
    },

    handleEdit(row) {
      this.isEdit = true
      this.dialogTitle = '编辑申请'
      this.applicationForm = {
        id: row.id,
        enterpriseId: row.enterpriseId,
        applicationType: row.applicationType,
        applicationContent: row.applicationContent,
        description: row.description,
        estimatedCompletionTime: row.estimatedCompletionTime,
        attachmentUrls: row.attachmentUrls || []
      }
      this.fileList = (row.attachmentUrls || []).map((url, index) => ({
        name: `附件${index + 1}`,
        url: url
      }))
      this.dialogVisible = true
    },

    handleView(row) {
      this.currentApplication = row
      this.detailDialogVisible = true
    },

    async handleProcess(row) {
      this.currentApplication = row
      this.processForm = {
        newStatus: '',
        remarks: '',
        estimatedCompletionTime: '',
        resultFileUrls: []
      }
      this.resultFileList = []
      await this.loadAvailableStatuses(row.status)
      this.processDialogVisible = true
    },

    handleStatusHistory(row) {
      this.currentApplication = row
      this.historyDialogVisible = true
      this.loadStatusHistory(row.id)
    },

    handleBatchProcess() {
      if (this.selectedApplications.length === 0) {
        this.$message.warning('请先选择要操作的申请')
        return
      }
      this.batchForm = {
        operation: '',
        processorId: null,
        targetStatus: '',
        remarks: ''
      }
      this.batchDialogVisible = true
    },

    handleStatusConfig() {
      this.$message.info('状态配置功能已简化，可直接在处理申请时选择状态')
    },

    handleFileSuccess(response) {
      if (response.success) {
        this.applicationForm.attachmentUrls.push(response.data.fileUrl)
      } else {
        this.$message.error('文件上传失败: ' + response.message)
      }
    },

    handleFileRemove(file) {
      if (file.response && file.response.success) {
        const index = this.applicationForm.attachmentUrls.indexOf(file.response.data.fileUrl)
        if (index > -1) {
          this.applicationForm.attachmentUrls.splice(index, 1)
        }
      }
    },

    handleResultFileSuccess(response) {
      if (response.success) {
        this.processForm.resultFileUrls.push(response.data.fileUrl)
      } else {
        this.$message.error('文件上传失败: ' + response.message)
      }
    },

    handleResultFileRemove(file) {
      if (file.response && file.response.success) {
        const index = this.processForm.resultFileUrls.indexOf(file.response.data.fileUrl)
        if (index > -1) {
          this.processForm.resultFileUrls.splice(index, 1)
        }
      }
    },

    async handleSubmit() {
      this.$refs.applicationForm.validate(async (valid) => {
        if (!valid) return false

        try {
          this.submitting = true
          let response
          if (this.isEdit) {
            response = await this.$http.put(`/enterprise-management/tax-applications/${this.applicationForm.id}`, this.applicationForm)
          } else {
            response = await this.$http.post('/enterprise-management/tax-applications', this.applicationForm)
          }

          if (response.success) {
            this.$message.success(this.isEdit ? '申请更新成功' : '申请提交成功')
            this.dialogVisible = false
            this.loadApplications()
          } else {
            this.$message.error('保存失败: ' + response.message)
          }
        } catch (error) {
          console.error('保存申请失败:', error)
          this.$message.error('保存申请失败')
        } finally {
          this.submitting = false
        }
      })
    },

    async handleProcessSubmit() {
      this.$refs.processForm.validate(async (valid) => {
        if (!valid) return false

        try {
          this.processing = true
          const response = await this.$http.put(`/enterprise-management/tax-applications/${this.currentApplication.id}/status`, this.processForm)

          if (response.success) {
            this.$message.success('状态更新成功')
            this.processDialogVisible = false
            this.loadApplications()
          } else {
            this.$message.error('状态更新失败: ' + response.message)
          }
        } catch (error) {
          console.error('状态更新失败:', error)
          this.$message.error('状态更新失败')
        } finally {
          this.processing = false
        }
      })
    },

    async handleBatchSubmit() {
      if (!this.batchForm.operation) {
        this.$message.warning('请选择操作类型')
        return
      }

      try {
        this.batchProcessing = true
        const applicationIds = this.selectedApplications.map(app => app.id)
        const response = await this.$http.post('/enterprise-management/tax-applications/batch-process', {
          applicationIds,
          ...this.batchForm
        })

        if (response.success) {
          this.$message.success('批量操作成功')
          this.batchDialogVisible = false
          this.loadApplications()
        } else {
          this.$message.error('批量操作失败: ' + response.message)
        }
      } catch (error) {
        console.error('批量操作失败:', error)
        this.$message.error('批量操作失败')
      } finally {
        this.batchProcessing = false
      }
    },

    handleDialogClose() {
      this.$refs.applicationForm.resetFields()
      this.applicationForm = {
        id: null,
        enterpriseId: null,
        applicationType: '',
        applicationContent: '',
        description: '',
        estimatedCompletionTime: '',
        attachmentUrls: []
      }
      this.fileList = []
    },

    getApplicationTypeText(type) {
      const types = {
        'TAX_REGISTRATION': '税务登记',
        'TAX_DECLARATION': '税务申报',
        'INVOICE_APPLICATION': '发票申请',
        'TAX_CANCELLATION': '税务注销',
        'OTHER': '其他'
      }
      return types[type] || type
    },

    getStatusText(status) {
      const statuses = {
        'PENDING': '待处理',
        'PROCESSING': '处理中',
        'COMPLETED': '已完成',
        'REJECTED': '已拒绝',
        'CANCELLED': '已取消'
      }
      return statuses[status] || status
    },

    getStatusType(status) {
      const types = {
        'PENDING': 'warning',
        'PROCESSING': 'primary',
        'COMPLETED': 'success',
        'REJECTED': 'danger',
        'CANCELLED': 'info'
      }
      return types[status] || 'info'
    },

    formatDateTime(dateTime) {
      if (!dateTime) return '-'
      return new Date(dateTime).toLocaleString('zh-CN')
    },

    formatJSON(jsonStr) {
      if (!jsonStr) return ''
      try {
        return JSON.stringify(JSON.parse(jsonStr), null, 2)
      } catch (e) {
        return jsonStr
      }
    }
  }
}
</script>

<style scoped lang="scss">
.tax-application-management {
  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;

    h2 {
      margin: 0;
      font-size: 24px;
      color: #333;
    }

    .actions {
      display: flex;
      gap: 10px;
    }
  }

  .search-card {
    margin-bottom: 20px;
  }
}

pre {
  background: #f5f5f5;
  padding: 10px;
  border-radius: 4px;
  font-size: 12px;
  max-height: 200px;
  overflow-y: auto;
}

@media screen and (max-width: 768px) {
  .tax-application-management {
    .header {
      flex-direction: column;
      align-items: flex-start;
      gap: 15px;

      h2 {
        font-size: 20px;
      }

      .actions {
        width: 100%;
        flex-wrap: wrap;

        button {
          flex: 1;
          min-width: 120px;
        }
      }
    }
  }
}
</style>